/**
 * 统一权限检查工具类
 * 提供标准化的权限检查逻辑，供所有微服务使用
 */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
/**
 * 统一权限检查函数
 * @param user 用户信息
 * @param requiredPermissions 所需权限列表
 * @returns 权限检查结果
 */
export function checkUserPermissions(user, requiredPermissions) {
    // 基础验证
    if (!user) {
        return {
            hasPermission: false,
            reason: "用户不存在",
            userPermissions: [],
            requiredPermissions: requiredPermissions
        };
    }
    if (!requiredPermissions || requiredPermissions.length === 0) {
        return {
            hasPermission: true,
            reason: "无需权限验证",
            userPermissions: user.permissions || [],
            requiredPermissions: []
        };
    }
    var userPermissions = user.permissions || [];
    // 1. 检查全局通配符权限
    if (userPermissions.includes("*")) {
        return {
            hasPermission: true,
            reason: "用户拥有全局通配符权限",
            matchedPermission: "*",
            userPermissions: userPermissions,
            requiredPermissions: requiredPermissions
        };
    }
    // 2. 检查具体权限匹配
    for (var _i = 0, requiredPermissions_1 = requiredPermissions; _i < requiredPermissions_1.length; _i++) {
        var permission = requiredPermissions_1[_i];
        var matchResult = checkPermissionMatch(permission, userPermissions);
        if (matchResult.hasMatch) {
            return {
                hasPermission: true,
                reason: matchResult.reason,
                matchedPermission: matchResult.matchedPermission,
                userPermissions: userPermissions,
                requiredPermissions: requiredPermissions
            };
        }
    }
    return {
        hasPermission: false,
        reason: "用户权限不足",
        userPermissions: userPermissions,
        requiredPermissions: requiredPermissions
    };
}
/**
 * 检查单个权限是否匹配
 */
function checkPermissionMatch(requiredPermission, userPermissions) {
    // 2.1 精确匹配
    if (userPermissions.includes(requiredPermission)) {
        return {
            hasMatch: true,
            reason: "\u7CBE\u786E\u5339\u914D\u6743\u9650: ".concat(requiredPermission),
            matchedPermission: requiredPermission
        };
    }
    // 2.2 服务级通配符匹配 (例如: msadmin:* 匹配 msadmin:admins:view)
    var servicePrefix = requiredPermission.split(":")[0];
    var serviceWildcard = "".concat(servicePrefix, ":*");
    if (userPermissions.includes(serviceWildcard)) {
        return {
            hasMatch: true,
            reason: "\u670D\u52A1\u901A\u914D\u7B26\u5339\u914D: ".concat(serviceWildcard, " \u5339\u914D ").concat(requiredPermission),
            matchedPermission: serviceWildcard
        };
    }
    // 2.3 功能级通配符匹配 (例如: msadmin:admins:* 匹配 msadmin:admins:view)
    var parts = requiredPermission.split(":");
    if (parts.length >= 3) {
        var functionPrefix = parts.slice(0, -1).join(":");
        var functionWildcard = "".concat(functionPrefix, ":*");
        if (userPermissions.includes(functionWildcard)) {
            return {
                hasMatch: true,
                reason: "\u529F\u80FD\u901A\u914D\u7B26\u5339\u914D: ".concat(functionWildcard, " \u5339\u914D ").concat(requiredPermission),
                matchedPermission: functionWildcard
            };
        }
    }
    return {
        hasMatch: false,
        reason: "\u6743\u9650\u4E0D\u5339\u914D: ".concat(requiredPermission)
    };
}
/**
 * 权限检查装饰器（用于微服务API）
 */
export function requirePermission(permission) {
    return function (_target, _propertyKey, descriptor) {
        var originalMethod = descriptor.value;
        descriptor.value = function () {
            var args = [];
            for (var _i = 0; _i < arguments.length; _i++) {
                args[_i] = arguments[_i];
            }
            return __awaiter(this, void 0, void 0, function () {
                var req, res, user, result;
                return __generator(this, function (_a) {
                    req = args[0];
                    res = args[1];
                    user = req.user || req.adminData;
                    result = checkUserPermissions(user, [permission]);
                    if (!result.hasPermission) {
                        return [2 /*return*/, res.status(403).json({
                                success: false,
                                message: "\u6743\u9650\u4E0D\u8DB3: ".concat(result.reason),
                                code: 'PERMISSION_DENIED',
                                details: result
                            })];
                    }
                    // 权限验证通过，继续执行原方法
                    return [2 /*return*/, originalMethod.apply(this, args)];
                });
            });
        };
        return descriptor;
    };
}
/**
 * 权限检查中间件工厂
 */
export function createSimplePermissionMiddleware(requiredPermissions) {
    return function (req, res, next) {
        var user = req.user || req.adminData;
        var result = checkUserPermissions(user, requiredPermissions);
        if (!result.hasPermission) {
            return res.status(403).json({
                success: false,
                message: "\u6743\u9650\u4E0D\u8DB3: ".concat(result.reason),
                code: 'PERMISSION_DENIED',
                details: result
            });
        }
        next();
    };
}
/**
 * 权限检查工具函数
 */
export var PermissionUtils = {
    /**
     * 检查用户是否有指定权限
     */
    hasPermission: function (user, permission) {
        return checkUserPermissions(user, [permission]).hasPermission;
    },
    /**
     * 检查用户是否有任一权限
     */
    hasAnyPermission: function (user, permissions) {
        return checkUserPermissions(user, permissions).hasPermission;
    },
    /**
     * 检查用户是否有所有权限
     */
    hasAllPermissions: function (user, permissions) {
        return permissions.every(function (permission) {
            return checkUserPermissions(user, [permission]).hasPermission;
        });
    },
    /**
     * 获取用户的服务权限
     */
    getServicePermissions: function (user, serviceName) {
        if (!user || !user.permissions)
            return [];
        return user.permissions.filter(function (permission) {
            return permission.startsWith("".concat(serviceName, ":")) || permission === "*";
        });
    },
    /**
     * 检查用户是否有服务权限
     */
    hasServicePermission: function (user, serviceName) {
        var servicePermissions = PermissionUtils.getServicePermissions(user, serviceName);
        return servicePermissions.length > 0;
    }
};
